/**
 * Duplicate-warning modal for the Client create/update paths.
 *
 * The backend `POST /api/clients` rejects with HTTP 409 + a payload
 * of { error: 'probable_duplicate', matches: [...] } when it detects a
 * name+phone or name+DOB collision. This component renders the matches
 * so the admin can:
 *   1. Cancel (close the dialog),
 *   2. Open the existing record (recommended path — no duplicate),
 *   3. Create anyway (retries the save with force=true).
 *
 * Used from ClinicView + Pipeline, wherever we call POST /clients.
 */
import React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

export default function DuplicateWarningDialog({ open, onOpenChange, matches, onCreateAnyway, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dup-warning-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#8A3A3A] flex items-center gap-2">
            <AlertTriangle size={20} /> Possible duplicate detected
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#685B72] leading-relaxed">
          The system found existing record(s) with a matching <b>name and phone/DOB</b>. Please review them before creating another profile.
        </p>
        <div className="border rounded-lg divide-y border-[#E9E3DA] max-h-64 overflow-auto" data-testid="dup-match-list">
          {(matches || []).map((m, i) => (
            <div key={i} className="p-3 flex items-start justify-between gap-3 hover:bg-[#FBF8F4]" data-testid={`dup-match-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#2D2833]">{m.client_name || '—'}</div>
                <div className="text-[11px] text-[#685B72] mt-0.5">
                  {m.kind === 'client'
                    ? <>Client · {m.clinic || '—'} · {m.stage || '—'}{m.phone ? ` · ${m.phone}` : ''}{m.dob ? ` · DOB ${m.dob}` : ''}</>
                    : <>Inquiry · {m.clinic || '—'} · {m.disposition || 'new'}{m.phone ? ` · ${m.phone}` : ''}{m.dob ? ` · DOB ${m.dob}` : ''}</>}
                </div>
              </div>
              {m.kind === 'client' && m.client_id && (
                <Link
                  to={`/pipeline?open=${m.client_id}`}
                  onClick={() => onOpenChange(false)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B08A3A] hover:underline shrink-0"
                  data-testid={`dup-open-${i}`}
                >
                  Open <ExternalLink size={11} />
                </Link>
              )}
            </div>
          ))}
          {(matches || []).length === 0 && (
            <div className="p-6 text-center text-sm text-[#685B72] italic">No matches to display.</div>
          )}
        </div>
        <DialogFooter className="flex justify-between gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="dup-cancel">Cancel — don't create</Button>
          <Button
            onClick={onCreateAnyway}
            className="bg-[#8A3A3A] hover:bg-[#6f2f2f] text-white"
            data-testid="dup-force-create"
          >
            Create anyway (not a duplicate)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
